/**
 * Çalma sırası yüzeyi (V5-07).
 *
 * Mini oynatıcıdan açılır; mevcut parça, sıradaki parçalar, karıştır/tekrar
 * ve yeniden sıralama burada yönetilir. Oynatıcı durumu kabukta yaşadığı
 * için panel kapanınca çalma kesilmez.
 */
import { type ReactNode, useRef } from 'react';
import { formatDuration } from '../format.ts';
import {
  ChevronDownIcon,
  ChevronUpIcon,
  CloseIcon,
  QueueIcon,
  RepeatIcon,
  ShuffleIcon,
  TrashIcon,
} from '../Icon.tsx';
import { resizeProxyImage } from '../imageSize.ts';
import { mediaUrl } from '../mediaToken.ts';
import { monogramGradient } from '../monogram.ts';
import type { AudioPlayerApi } from '../useAudioPlayer.ts';
import { useModalFocus } from '../useModalFocus.ts';

function repeatLabel(repeat: AudioPlayerApi['repeat']): string {
  if (repeat === 'one') return 'Tek parça';
  if (repeat === 'all') return 'Tümü';
  return 'Kapalı';
}

export function QueuePanel({
  player,
  onClose,
}: {
  player: AudioPlayerApi;
  onClose: () => void;
}): ReactNode {
  const panelRef = useRef<HTMLDivElement>(null);
  useModalFocus(panelRef, onClose);

  if (player.queue.length === 0) return null;

  return (
    <div className="queue-panel">
      <button
        type="button"
        className="queue-panel__scrim"
        onClick={onClose}
        aria-label="Kapat"
        tabIndex={-1}
      />
      <div
        ref={panelRef}
        className="queue-panel__card"
        role="dialog"
        aria-modal="true"
        aria-labelledby="queue-title"
        tabIndex={-1}
      >
        <div className="queue-panel__header">
          <div className="queue-panel__heading">
            <span className="queue-panel__heading-icon" aria-hidden="true">
              <QueueIcon size={18} />
            </span>
            <div>
              <h2 id="queue-title" className="queue-panel__title">
                Çalma sırası
              </h2>
              <p className="queue-panel__count">{player.queue.length} parça</p>
            </div>
          </div>
          <button
            type="button"
            className="queue-panel__close"
            onClick={onClose}
            aria-label="Çalma sırasını kapat"
            title="Kapat"
          >
            <CloseIcon size={18} />
          </button>
        </div>

        <div className="queue-panel__modes">
          <button
            type="button"
            className="button button--ghost queue-panel__mode"
            aria-pressed={player.shuffle}
            onClick={player.toggleShuffle}
          >
            <ShuffleIcon size={15} />
            Karıştır
          </button>
          <button
            type="button"
            className="button button--ghost queue-panel__mode"
            aria-pressed={player.repeat !== 'off'}
            onClick={player.cycleRepeat}
            title={`Tekrar: ${repeatLabel(player.repeat)}`}
          >
            <RepeatIcon size={15} />
            Tekrar: {repeatLabel(player.repeat)}
          </button>
          <button
            type="button"
            className="button button--ghost queue-panel__clear"
            onClick={player.clearQueue}
            disabled={player.queue.length <= 1}
          >
            Sırayı temizle
          </button>
        </div>

        <ol className="queue-panel__list" aria-label="Çalma sırası parçaları">
          {player.queue.map((item, index) => {
            const isCurrent = index === player.queueIndex;
            return (
              <li key={item.id} className={`queue-panel__item ${isCurrent ? 'is-current' : ''}`}>
                <button
                  type="button"
                  className="queue-panel__main"
                  onClick={() => player.playQueueIndex(index)}
                  aria-current={isCurrent ? 'true' : undefined}
                  title={isCurrent ? 'Şu an çalıyor' : 'Bu parçayı çal'}
                >
                  <span
                    className="queue-panel__cover"
                    style={{
                      backgroundImage: `url(${
                        resizeProxyImage(item.posterUrl, 'w185') ??
                        item.thumbnailUrl ??
                        mediaUrl(`/api/items/${item.id}/thumbnail`)
                      }), ${monogramGradient(item.id)}`,
                    }}
                    aria-hidden="true"
                  />
                  <span className="queue-panel__meta">
                    <span className="queue-panel__item-title">{item.title}</span>
                    <span className="queue-panel__item-sub">
                      {isCurrent ? 'Şu an çalıyor' : formatDuration(item.durationMs)}
                      {item.year !== null && !isCurrent ? ` · ${item.year}` : ''}
                    </span>
                  </span>
                </button>

                <div className="queue-panel__actions">
                  <button
                    type="button"
                    className="queue-panel__action"
                    onClick={() => player.moveQueueItem(index, index - 1)}
                    disabled={index === 0}
                    aria-label={`${item.title} parçasını yukarı taşı`}
                    title="Yukarı taşı"
                  >
                    <ChevronUpIcon size={15} />
                  </button>
                  <button
                    type="button"
                    className="queue-panel__action"
                    onClick={() => player.moveQueueItem(index, index + 1)}
                    disabled={index === player.queue.length - 1}
                    aria-label={`${item.title} parçasını aşağı taşı`}
                    title="Aşağı taşı"
                  >
                    <ChevronDownIcon size={15} />
                  </button>
                  <button
                    type="button"
                    className="queue-panel__action queue-panel__action--danger"
                    onClick={() => player.removeFromQueue(item.id)}
                    disabled={isCurrent}
                    aria-label={`${item.title} parçasını sıradan çıkar`}
                    title={isCurrent ? 'Çalan parça sıradan çıkarılamaz' : 'Sıradan çıkar'}
                  >
                    <TrashIcon size={14} />
                  </button>
                </div>
              </li>
            );
          })}
        </ol>
      </div>
    </div>
  );
}
